import { FC, useEffect, useState } from 'react';
import { Redirect, Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import { MapDispatchToPropsFunction, connect } from 'react-redux';

import { CardI } from '~src/interfaces/question.interface';
import { CycleI, PackName } from '~src/interfaces/cycle.interface';
import { packToUri } from '~src/modules/questions/queryParameters';
import PageLoader from '~src/shared/pageLoader/PageLoader';
import { CardsContainer } from '~src/modules/questions/components/shared.sc';
import { itemIdMatch } from '~src/constants';
import { baseBlue } from '~src/styles/colors';
import RedirectCard, { CyclePack } from '~src/modules/questions/components/cards/RedirectCard';
import { getQuestions } from '~src/modules/questions/reducers/questionsReducer';
import { fetchScales } from '~src/modules/scales/actions/scaleActions';
import { FetchScalesParams, Scale, ScaleIncludeParam } from '~src/interfaces/scale.interface';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { StoreI } from '~src/rootReducer';
import { routes } from '~src/routingConfig';
import { ComponentRouterContainer as QuestionsRouterContainer } from '~src/styles/shared.sc';

import { Centering } from './questions.sc';
import Questions from './Questions';
import { fetchCycles } from '~src/shared/sidebar/actions/sidebarActions';

interface StateProps {
  questions: CardI[];
  cycles: Record<string, CycleI>;
  isLoadingCycles: boolean;
  loading: boolean;
}

interface DispatchProps {
  fetchScales: (params: FetchScalesParams) => ApiReduxResp<Scale[]>;
  fetchCycles: () => ApiReduxResp<CycleI[]>;
}

interface OwnProps {
  isAuthenticated: boolean;
}

type Props = DispatchProps & StateProps & OwnProps & RouteComponentProps<{ cycleId?: string }>;

export function getFirstUnansweredCyclePackname(
  cycles: CycleI[],
): { cycleId: string; packName: PackName; precedent?: string } | undefined {
  let result;
  cycles.some((cycle) => {
    const unfinishedPack = cycle.packs.find((p) => !p.finished);
    if (!unfinishedPack) {
      return false;
    }

    result = { cycleId: cycle.id, packName: unfinishedPack.name };
  });

  return result;
}

function hasUnfinishedCycle(cycles: Record<string, CycleI>): boolean {
  return Object.values(cycles).filter((c) => c.packs.some((p) => !p.finished)).length > 0;
}

const QuestionsRouter: FC<Props> = (props) => {
  const [scalesLoading, setScalesLoading] = useState(true);

  useEffect(() => {
    void props.fetchCycles();

    void props
      .fetchScales({
        include: `${ScaleIncludeParam.ScaleDeleted},${ScaleIncludeParam.ScaleVersion},${ScaleIncludeParam.ScaleCustom}`,
        per_page: 1000,
      })
      .then(() => {
        setScalesLoading(false);
      });
  }, []);

  const getFirstUnansweredLink = (cycles: CycleI[]) => {
    let linkString = routes.answerQuestions.cycle;

    const unfinishedPack = getFirstUnansweredCyclePackname(cycles);
    if (unfinishedPack) {
      linkString = packToUri(unfinishedPack);
    }

    return linkString;
  };

  const defaultRoute = (): JSX.Element => {
    let redirectPath = routes.answerQuestions.noSurvey;

    if (hasUnfinishedCycle(props.cycles)) {
      const linkString = getFirstUnansweredLink(Object.values(props.cycles));
      if (props.location.pathname !== linkString) {
        redirectPath = linkString;
      }
    }
    return <Redirect to={redirectPath} />;
  };

  const renderQuestions = (props: RouteComponentProps<{ cycleId: string }>) => {
    console.info('QuestionsRouter: rendering Questions with cycleId ' + props.match.params.cycleId);
    return (
      <Questions
        cycleId={props.match.params.cycleId}
        key={`${props.match.params.cycleId}.${props.location.search || 'no_pack'}`}
      />
    );
  };

  const onRedirect = (cyclePack?: CyclePack) => {
    let path = routes.answerQuestions.questions;
    if (cyclePack) {
      path = `${routes.answerQuestions.cycle}/${cyclePack.cycleId}?selected_pack=${cyclePack.packName}`;
    }
    props.history.replace(path);
  };

  const renderRedirect = () => <RedirectCard onRedirect={onRedirect} />;

  const isLoading = scalesLoading || !props.isLoadingCycles || props.loading;

  return (
    <QuestionsRouterContainer>
      {isLoading ? (
        <CardsContainer>
          <Centering>
            <PageLoader secondColor={baseBlue.a5} />
          </Centering>
        </CardsContainer>
      ) : (
        <Switch>
          {Object.values(props.cycles).length > 0 && (
            <Route
              exact
              path={`${props.match.url}/cycle/:cycleId(${itemIdMatch})`}
              render={renderQuestions}
            />
          )}
          <Route exact path={`${props.match.url}/cycle/finished`} render={renderRedirect}></Route>
          {defaultRoute()}
        </Switch>
      )}
    </QuestionsRouterContainer>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  questions: getQuestions(state),
  cycles: state.user.cycles.byId || {},
  isLoadingCycles: state.user.cycles.initialLoaded,
  loading: state.user.cycles.loading,
});

const mapDispatchToProps: MapDispatchToPropsFunction<DispatchProps, OwnProps> = (dispatch) => ({
  fetchScales: (params) => dispatch(fetchScales(params)),
  fetchCycles: () => dispatch(fetchCycles()),
});

const QuestionsRouterWithRouter = withRouter(QuestionsRouter);
const QuestionsRouterSuper = connect(mapStateToProps, mapDispatchToProps)(QuestionsRouterWithRouter);

export default QuestionsRouterSuper;
